import moment from "moment";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-smart-toaster";
import axios from "axios";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth0 } from "@auth0/auth0-react";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { FromLoader } from "../../../components/LoaderC";
import Loader from "../../../components/Loader";
import styles from "./add-tenant/addTenant.module.css";
import StepperHeader from "./add-tenant/StepperHeader";
import StepUnit from "./add-tenant/StepUnit";
import StepTenants from "./add-tenant/StepTenants";
import StepLease from "./add-tenant/StepLease";
import { sortList, toAmount } from "./add-tenant/fields";

const STEPS = ["Unit & lease type", "Tenants", "Lease & payment"];

const phoneRegExp = /^\(?([0-9]{3})\)?[ ]?([0-9]{3})[-]?([0-9]{4})$/;

const tenantSchema = {
  old_id: yup.string().nullable(),
  type: yup.string().nullable(),
  first_name: yup.string().required("This field is Required").max(50, "First name cannot be more than 50 characters"),
  last_name: yup.string().required("This field is Required").max(50, "Last name cannot be more than 50 characters"),
  email: yup.string().required("This field is Required").email("Invalid E-mail").max(50, "Email cannot be more than 50 characters"),
  phone: yup.string().required("This field is Required").matches(phoneRegExp, "Invalid phone number"),
};

const amountSchema = (label, allowZero = false) => yup.string().required("This field is Required")
  .test("non-zero-validate", "This field should be $1 or greater.", (value) => allowZero || toAmount(value) >= 1)
  .test("max-4-digit", `${label} can not be more than 6 digits.`, (value) => toAmount(value) < 10000);

const isSelected = (value) => value !== undefined && value !== null && value !== "" && value !== 0;

const notInFuture = (value) =>!value || !dayjs(value).isAfter(dayjs(), "day");

// API values for lease_type (tenants-new).
const LEASE_TYPE_API = { New: "new", Renewal: "renewal", Existing: "existing" };

// Fields validated before leaving each step; everything else is validated on Add.
const STEP_FIELDS = {
  1: () => ["property_id", "apt_id", "lease_type"],
  2: (count) => Array.from({ length: count }).flatMap((_, index) => ["first_name", "last_name", "email", "phone"].map((key) => `tenants.${index}.${key}`)),
};

const stepForField = (field) => {
  if (["property_id", "apt_id", "lease_type"].includes(field)) return 1;
  if (field.startsWith("tenants") || field.startsWith("phone") || field.startsWith("email")) return 2;
  return 3;
};

const AddForm = ({ closeAddForm, addTenant, setSnackbarOpen, setSnackbarMsg, setOpenAddForm }) => {
  const { getAccessTokenSilently } = useAuth0();

  const [step, setStep] = useState(1);
  // Kept in state (not watch) so the type note and step 3 fields always follow the select.
  const [leaseType, setLeaseType] = useState("");
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [apartmentList, setApartmentList] = useState([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [LeaseDocumentList, setLeaseDocumentList] = useState([]);
  const [LeaseDocumentOptions, setLeaseDocumentOptions] = useState([]);
  const [selectDocument, setSelectDocument] = useState([]);
  const [selectDocumentIds, setSelectDocumentIds] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(0);
  const [selectedApartment, setSelectedApartment] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [propertyList, setPropertyList] = useState([]);
  const [sortedLeaseDocuments, setSortedLeaseDocuments] = useState(sortList([]));
  const [prospectList, setProspectList] = useState([]);
  const [movedTeanntList, setMovedTeanntList] = useState([]);

  const schema = yup.object().shape({
    property_id: yup.mixed().test("required", "This field is Required", isSelected),
    apt_id: yup.mixed().test("required", "This field is Required", isSelected),
    lease_type: yup.mixed().required("This field is Required").oneOf(["New", "Renewal", "Existing"], "This field is Required"),
    tenants: yup.array().of(yup.object().shape(tenantSchema)),
    lease_term: yup.mixed().test("required", "This field is Required", isSelected),
    rent: amountSchema("Rent"),
    security_amount: yup.string().when("lease_type", (lease_type) => amountSchema("Security Amount", lease_type === "Existing")),
    start_date: yup.date().typeError("This field is Required").required("This field is Required"),
    end_date: yup.date().typeError("This field is Required").required("This field is Required")
      .when("start_date", (start_date, schema) => (start_date ? schema.test("after-start", "Lease end date must be after the start date.", (value) => !value || dayjs(value).isAfter(dayjs(start_date), "day")) : schema)),
    move_in_date: yup.mixed().when("lease_type", (lease_type) => {
      if (lease_type === "New")
        return yup.date().typeError("This field is Required").required("This field is Required");
      if (lease_type === "Existing")
        return yup.date().typeError("This field is Required").required("This field is Required").test("not-future", "The move in date cannot be in the future for an existing lease.", notInFuture);
      return yup.mixed().nullable();
    }),
    first_rent: yup.string().when("lease_type", (lease_type) => {
      if (lease_type === "New") return amountSchema("First Month Rent");
      return yup.string().nullable();
    }),
    new_security_amount: yup.string().when("lease_type", (lease_type) => {
      if (lease_type === "Renewal") return amountSchema("New Security Amount");
      return yup.string().nullable();
    }),
    payment_received: yup.string().when("lease_type", (lease_type) => {
      if (lease_type === "Renewal") return yup.string().required("This field is Required");
      return yup.string().nullable();
    }),
    payment_date: yup.mixed().when(["lease_type", "payment_received"], (lease_type, payment_received) => {
      if (lease_type === "Renewal" && payment_received === "Yes")
        return yup.date().typeError("This field is Required").required("This field is Required");
      return yup.mixed().nullable();
    }),
    security_received: yup.boolean().nullable(),
    security_received_date: yup.mixed().when(["lease_type", "security_received"], (lease_type, security_received) => {
      if (lease_type === "Existing" && security_received)
        return yup.date().typeError("This field is Required").required("This field is Required").test("not-future", "The received date cannot be in the future.", notInFuture);
      return yup.mixed().nullable();
    }),
    is_existing: yup.mixed().when("lease_type", (lease_type) => {
      if (lease_type === "Existing") return yup.mixed().nullable();
      return yup.mixed().test("required", "This field is Required", isSelected);
    }),
    docume_ids: yup.mixed().when(["lease_type", "is_existing"], (lease_type, is_existing) => {
      if (lease_type !== "Existing" && is_existing === "No")
        return yup.array().required("This field is Required").min(1, "This field is Required").nullable();
      return yup.mixed().nullable();
    }),
    document_files: yup.mixed().when(["lease_type", "is_existing"], (lease_type, is_existing) => {
      if (lease_type === "Existing" || is_existing === "Yes")
        return yup.mixed().test("required", "Please upload the lease document.", () => uploadFiles.length !== 0);
      return yup.mixed().nullable();
    }),
  });

  const { register, handleSubmit, control, errors, setValue, getValues, clearErrors, watch, trigger } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      tenants: [{ first_name: "", last_name: "", email: "", phone: "", old_id: "", type: "" }],
      lease_term: "",
      lease_type: ""
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "tenants" });

  useEffect(() => {
    register("document_files");
    register("docume_ids");
  }, [register]);

  useEffect(() => {
    async function fetchPropertyList() {
      setFormLoading(true);
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios(process.env.REACT_APP_TENANT_API_URL + "v1/property-apt-list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }).then((response) => {
        setFormLoading(false);
        setPropertyList(response.data.data);
      }).catch((error) => {
        setFormLoading(false);
      });
    }
    fetchPropertyList();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    async function fetchData() {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      axios.get(process.env.REACT_APP_PAYMENT_API_URL + "v1/document-list/add-lease", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }).then((response) => {
        setLeaseDocumentList(response.data.data);
      }).catch((error) => {
        setLeaseDocumentList([]);
      });
    }
    fetchData();
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const fetchProspect = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      await axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/prospect-list/" + selectedProperty, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then((response) => {
        setProspectList(response.data.data);
      }).catch((error) => { });
    }

    const fetchMoveTenants = async () => {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      await axios.get(process.env.REACT_APP_TENANT_API_URL + "v1/moved-out-tenants", {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      }).then((response) => {
        setMovedTeanntList(response.data.data);
      }).catch((error) => { });
    }

    if (selectedProperty > 0) {
      fetchProspect();
      fetchMoveTenants();
    }
  }, [selectedProperty, getAccessTokenSilently]);

  const watched = watch(["rent", "start_date", "move_in_date", "security_amount", "security_received", "security_received_date", "payment_received", "is_existing"]);

  // First month's rent is prorated when a new tenant moves in on a different day than the lease starts.
  useEffect(() => {
    if (!selectedApartment || leaseType !== "New") return;

    const aptObj = apartmentList?.find((i) => i.value === selectedApartment);
    if (!aptObj) return;

    const monthlyRent = watched.rent ? String(watched.rent).replace(/,/g, "") : aptObj?.rent;

    if (watched.start_date && watched.move_in_date) {
      const startDateF = dayjs(watched.start_date).format('YYYY-MM-DD');
      const moveInDateF = dayjs(watched.move_in_date).format('YYYY-MM-DD');

      if (startDateF !== moveInDateF) {
        const totaldateOfMonth = dayjs(watched.move_in_date).daysInMonth();
        const remainingDateOfMonth = dayjs(watched.move_in_date).endOf('month').diff(dayjs(watched.move_in_date), 'day') + 1;
        setValue('first_rent', Math.round((monthlyRent / totaldateOfMonth) * remainingDateOfMonth));
      } else {
        setValue('first_rent', monthlyRent);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApartment, leaseType, watched.start_date, watched.move_in_date, watched.rent]);

  useEffect(() => {
    setSelectDocumentIds(selectDocument.map((i) => i.id));
    setSortedLeaseDocuments(selectDocument.map((i, index) => ({ ...i, order: index, index: index })));
  }, [selectDocument]);

  const selectProperty = (pId) => {
    setSelectedProperty(pId);
    const propObj = propertyList?.find((i) => i.value === pId);
    if (propObj) {
      if (propObj?.apartments) {
        setApartmentList(propObj?.apartments);
        setSelectedApartment(0);
        setValue("apt_id", "");
        setValue("security_amount", "");
        setValue("rent", "");
      }

      setLeaseDocumentOptions(LeaseDocumentList.filter((i) => i.property_id === pId));
      setSelectDocument([]);
      setValue("docume_ids", []);
    }
  };

  const selectApartment = (aId) => {
    setSelectedApartment(aId);
    const aptObj = apartmentList?.find((i) => i.value === aId);
    if (aptObj) {
      setValue("security_amount", aptObj?.rent);
      setValue("rent", aptObj?.rent);
      clearErrors(["security_amount", "rent"]);
    }

    if (!getValues("lease_type")) {
      setValue("lease_type", "New", { shouldValidate: true });
      setLeaseType("New");
    }
    if (!getValues("lease_term")) {
      setValue("lease_term", "1 Year");
    }
  };

  const changeLeaseType = (value) => {
    setLeaseType(value);
    clearErrors(["move_in_date", "first_rent", "new_security_amount", "payment_received", "payment_date", "security_received_date", "is_existing", "docume_ids", "document_files", "end_date", "security_amount"]);
  };

  const selectDocuments = (event) => {
    const { target: { value } } = event;
    setSelectDocument(value);
    setValue("docume_ids", value);
    clearErrors(`docume_ids`);
  };

  const reorderList = (sourceIndex, destinationIndex) => {
    if (destinationIndex === sourceIndex) {
      return;
    }
    const list = sortedLeaseDocuments;

    if (destinationIndex === 0) {
      list[sourceIndex].order = list[0].order - 1;
      setSortedLeaseDocuments(sortList(list));
      return;
    }

    if (destinationIndex === list.length - 1) {
      list[sourceIndex].order = list[list.length - 1].order + 1;
      setSortedLeaseDocuments(sortList(list));
      return;
    }

    if (destinationIndex < sourceIndex) {
      list[sourceIndex].order = (list[destinationIndex].order + list[destinationIndex - 1].order) / 2;
      setSortedLeaseDocuments(sortList(list));
      return;
    }

    list[sourceIndex].order = (list[destinationIndex].order + list[destinationIndex + 1].order) / 2;
    setSortedLeaseDocuments(sortList(list));
  }

  const fileHandler = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    const fileNameExt = file.name.substr(file.name.lastIndexOf(".") + 1);
    if (fileNameExt.toLowerCase() !== "pdf") {
      toast.error("File type is not allowed.");
    } else if (file.size > 10485760) {
      toast.error("File size should not be greater than 10 MB.");
    } else {
      setUploadFiles([file]);
      clearErrors("document_files");
    }
  };

  const removeFile = () => {
    setUploadFiles([]);
  };

  const tenantOptions = [...prospectList.map(item => ({
    ...item, type: "prospect", typeL: "Prospect"
  })), ...movedTeanntList.map(item => ({
    ...item, type: "tenant", typeL: "Moved Out"
  }))].sort((a, b) => a.label.localeCompare(b.label));

  const goToStep = (stepNo) => {
    setStep(stepNo);
  };

  const goNext = async () => {
    const isValid = await trigger(STEP_FIELDS[step](fields.length));
    if (isValid) {
      setStep((prev) => Math.min(STEPS.length, prev + 1));
    }
  };

  const onInvalid = (formErrors) => {
    const firstStep = Math.min(...Object.keys(formErrors).map(stepForField));
    if (firstStep < step) setStep(firstStep);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    const isExistingLease = data.lease_type === "Existing";
    const formatDate = (value) => moment(dayjs(value).toDate()).format("YYYY-MM-DD");

    const formData = new FormData();

    formData.append("lease_type", LEASE_TYPE_API[data.lease_type]);
    formData.append("property_id", data.property_id);
    formData.append("apt_id", data.apt_id);
    formData.append("tenants", JSON.stringify(data.tenants));
    formData.append("lease_term", data.lease_term);
    formData.append("rent", String(data.rent).replace(/,/g, ""));
    formData.append("security_amount", String(data.security_amount).replace(/,/g, ""));
    formData.append("start_date", formatDate(data.start_date));
    formData.append("end_date", formatDate(data.end_date));
    formData.append("is_renew", data.lease_type === "Renewal" ? 1 : 0);

    if (data.move_in_date) {
      formData.append("move_in_date", formatDate(data.move_in_date));
    }

    if (isExistingLease) {
      formData.append("is_esign", 0);
      formData.append("uploadFile", uploadFiles[0]);
      formData.append("security_received", data.security_received ? 1 : 0);
      if (data.security_received) {
        formData.append("security_received_date", formatDate(data.security_received_date));
      }
    } else {
      formData.append("is_esign", data.is_existing === "Yes" ? 0 : 1);

      if (data.lease_type === "New") {
        formData.append("first_rent", String(data.first_rent).replace(/,/g, ""));
      }

      if (data.is_existing === "Yes") {
        formData.append("uploadFile", uploadFiles[0]);
      } else {
        formData.append("docume_ids", sortedLeaseDocuments.map((i) => i.id).join());
      }

      if (data.lease_type === "Renewal") {
        let newSecurityAmount = toAmount(data.new_security_amount) - toAmount(data.security_amount);
        formData.append("new_security_amount", newSecurityAmount < 0 ? 0 : newSecurityAmount);
        formData.append("is_payment", data.payment_received === "Yes" ? 1 : 0);
        if (data.payment_received === "Yes") {
          formData.append("payment_date", formatDate(data.payment_date));
        }
      }
    }

    axios.post(process.env.REACT_APP_TENANT_API_URL + "v1/tenants-new", formData, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).then((response) => {
      setLoading(false);
      setOpenAddForm(false);

      setSnackbarMsg(response.data.message);
      setSnackbarOpen(true);

      addTenant(response.data.data);
    }).catch((error) => {
      if (typeof error.response !== "undefined") {
        toast.error(error.response.data.message);

        const failedFields = Object.keys(error.response.data?.messages || {});
        if (failedFields.length) {
          setStep(Math.min(...failedFields.map(stepForField)));
        }
      }
      setLoading(false);
    });
  };

  const submitForm = (e) => {
    // Enter on an earlier step moves forward instead of submitting the whole form.
    if (step < STEPS.length) {
      e.preventDefault();
      goNext();
      return;
    }
    handleSubmit(onSubmit, onInvalid)(e);
  };

  return (<>
    {loading && <Loader />}
    {formLoading && (<div className="Loader FromLoader"><FromLoader /></div>)}
    {!formLoading && (<form onSubmit={submitForm} className={styles.Form} noValidate>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <StepperHeader steps={STEPS} step={step} goToStep={goToStep} />

        <div className={step === 1 ? "" : styles.Hidden}>
          <StepUnit
            control={control}
            errors={errors}
            propertyList={propertyList}
            apartmentList={apartmentList}
            selectedProperty={selectedProperty}
            selectedApartment={selectedApartment}
            leaseType={leaseType}
            onPropertyChange={selectProperty}
            onApartmentChange={selectApartment}
            onLeaseTypeChange={changeLeaseType}
          />
        </div>

        <div className={step === 2 ? "" : styles.Hidden}>
          <StepTenants
            control={control}
            errors={errors}
            fields={fields}
            append={append}
            remove={remove}
            setValue={setValue}
            clearErrors={clearErrors}
            tenantOptions={tenantOptions}
            selectedProperty={selectedProperty}
          />
        </div>

        <div className={step === 3 ? "" : styles.Hidden}>
          <StepLease
            control={control}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
            clearErrors={clearErrors}
            leaseType={leaseType}
            selectedApartment={selectedApartment}
            watched={watched}
            uploadFiles={uploadFiles}
            fileHandler={fileHandler}
            removeFile={removeFile}
            leaseDocumentOptions={LeaseDocumentOptions}
            selectDocument={selectDocument}
            selectDocuments={selectDocuments}
            selectDocumentIds={selectDocumentIds}
            dropdownOpen={dropdownOpen}
            setDropdownOpen={setDropdownOpen}
            sortedLeaseDocuments={sortedLeaseDocuments}
            reorderList={reorderList}
          />
        </div>

        <div className={styles.Footer}>
          <span className={styles.StepCount}>Step {step} of {STEPS.length}</span>
          {step === 1 && (<button type="button" className="CancelPopupBU" onClick={() => closeAddForm(false)}>Cancel</button>)}
          {step > 1 && (<button type="button" className={styles.OutlineBU} onClick={() => setStep(step - 1)}>Back</button>)}
          {step < STEPS.length && (<button type="button" className="SubmitPopupBU" onClick={goNext}>Next</button>)}
          {step === STEPS.length && (<button type="submit" className="SubmitPopupBU">Add</button>)}
        </div>
      </LocalizationProvider>
    </form>)}
  </>);
};

export default AddForm;
